import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { PLACEHOLDER_EMAIL } from '../lib/contactDefaults';
import { Button } from '../components/ui/button';
import { BackToDiary } from '../components/BackToDiary';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Loader2,
  Grid3X3,
  LayoutList,
  Download,
  Filter,
  UserPlus,
  Briefcase,
  PoundSterling,
  Calendar as CalendarIcon,
  Palmtree,
  Clock,
  FolderKanban,
  ClipboardList
} from 'lucide-react';
import {
  format,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  addWeeks,
  subWeeks,
  addMonths,
  subMonths,
  isToday,
  parseISO,
  differenceInDays
} from 'date-fns';

// Import extracted components
import { VacationTab, TimeReportsTab, ScheduleWeekView, ScheduleMonthView, ScheduleListView, ProjectFilterSidebar, ScheduleFlagsCard, compareProjectRefs } from '../components/schedule';
import { ReconcilePanel } from './portal/PortalTeamSchedule';
import { computeScheduleFlags, flattenSchedules } from '../utils/scheduleFlags';

// Employee color palette
const EMPLOYEE_COLORS = [
  { bg: 'bg-blue-500/20', border: 'border-blue-500/50', text: 'text-blue-400', hex: '#3B82F6' },
  { bg: 'bg-emerald-500/20', border: 'border-emerald-500/50', text: 'text-emerald-400', hex: '#10B981' },
  { bg: 'bg-purple-500/20', border: 'border-purple-500/50', text: 'text-purple-400', hex: '#8B5CF6' },
  { bg: 'bg-pink-500/20', border: 'border-pink-500/50', text: 'text-pink-400', hex: '#EC4899' },
  { bg: 'bg-amber-500/20', border: 'border-amber-500/50', text: 'text-amber-400', hex: '#F59E0B' },
  { bg: 'bg-cyan-500/20', border: 'border-cyan-500/50', text: 'text-cyan-400', hex: '#06B6D4' },
  { bg: 'bg-red-500/20', border: 'border-red-500/50', text: 'text-red-400', hex: '#EF4444' },
  { bg: 'bg-indigo-500/20', border: 'border-indigo-500/50', text: 'text-indigo-400', hex: '#6366F1' },
];

// Project color palette — used by ScheduleListView left border + dot.
// Same hex values as EMPLOYEE_COLORS for visual harmony, but kept in a
// separate palette so identical employee/project colours are intentional.
const PROJECT_COLORS = [
  { bg: 'bg-blue-500/15',    border: 'border-blue-500/50',    text: 'text-blue-300',    borderHex: '#3B82F6', dot: 'bg-blue-500',    name: 'blue' },
  { bg: 'bg-emerald-500/15', border: 'border-emerald-500/50', text: 'text-emerald-300', borderHex: '#10B981', dot: 'bg-emerald-500', name: 'emerald' },
  { bg: 'bg-purple-500/15',  border: 'border-purple-500/50',  text: 'text-purple-300',  borderHex: '#8B5CF6', dot: 'bg-purple-500',  name: 'purple' },
  { bg: 'bg-amber-500/15',   border: 'border-amber-500/50',   text: 'text-amber-300',   borderHex: '#F59E0B', dot: 'bg-amber-500',   name: 'amber' },
  { bg: 'bg-pink-500/15',    border: 'border-pink-500/50',    text: 'text-pink-300',    borderHex: '#EC4899', dot: 'bg-pink-500',    name: 'pink' },
  { bg: 'bg-cyan-500/15',    border: 'border-cyan-500/50',    text: 'text-cyan-300',    borderHex: '#06B6D4', dot: 'bg-cyan-500',    name: 'cyan' },
  { bg: 'bg-red-500/15',     border: 'border-red-500/50',     text: 'text-red-300',     borderHex: '#EF4444', dot: 'bg-red-500',     name: 'red' },
  { bg: 'bg-indigo-500/15',  border: 'border-indigo-500/50',  text: 'text-indigo-300',  borderHex: '#6366F1', dot: 'bg-indigo-500',  name: 'indigo' },
  { bg: 'bg-teal-500/15',    border: 'border-teal-500/50',    text: 'text-teal-300',    borderHex: '#14B8A6', dot: 'bg-teal-500',    name: 'teal' },
  { bg: 'bg-orange-500/15',  border: 'border-orange-500/50',  text: 'text-orange-300',  borderHex: '#F97316', dot: 'bg-orange-500',  name: 'orange' },
  { bg: 'bg-lime-500/15',    border: 'border-lime-500/50',    text: 'text-lime-300',    borderHex: '#84CC16', dot: 'bg-lime-500',    name: 'lime' },
  { bg: 'bg-fuchsia-500/15', border: 'border-fuchsia-500/50', text: 'text-fuchsia-300', borderHex: '#D946EF', dot: 'bg-fuchsia-500', name: 'fuchsia' },
];

const TRADE_OPTIONS = [
  'Electrician', 'Plumber', 'Carpenter', 'Plasterer', 'Bricklayer',
  'Roofer', 'Painter', 'Labourer', 'Site Manager', 'Groundworker', 'Other'
];

const ROLE_OPTIONS = [
  { value: 'admin', label: 'Admin' },
  { value: 'office', label: 'Office' },
  { value: 'site_operative', label: 'Site Operative' }
];

export function Schedule() {
  const [schedules, setSchedules] = useState([]);
  const [projects, setProjects] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [team, setTeam] = useState([]); // admin's own contractors (Settings → team); empty → flags cover all field staff
  const [vacationRequests, setVacationRequests] = useState([]);
  const [timeReports, setTimeReports] = useState({ entries: [], employee_summaries: [], total_hours: 0 });
  const [timeReportEmployees, setTimeReportEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeReportsLoading, setTimeReportsLoading] = useState(false);
  const [currentDate, setCurrentDate] = useState(() => {
    // Deep link from the Site Diary: /schedule?project=<id>&date=YYYY-MM-DD
    const d = new URLSearchParams(window.location.search).get('date');
    const parsed = d ? new Date(`${d}T00:00:00`) : null;
    return parsed && !isNaN(parsed) ? parsed : new Date();
  });
  // viewMode: 'list' (new default — employee × day grid), 'week' (project grid), 'month'
  // Persisted per-browser in localStorage so users keep their preferred view.
  const [viewMode, setViewMode] = useState(() => {
    try {
      const saved = localStorage.getItem('lux_schedule_view');
      if (saved && ['list', 'week', 'month'].includes(saved)) return saved;
    } catch {} // eslint-disable-line no-empty
    return 'list';
  });
  const [activeTab, setActiveTab] = useState('schedule'); // 'schedule', 'vacation', 'time-reports', 'reconcile'
  const [reconcileWeek, setReconcileWeek] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  
  // Time reports filters
  const [timeReportFilters, setTimeReportFilters] = useState({
    start_date: format(startOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd'),
    end_date: format(endOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd'),
    employee_id: ''
  });
  
  // Dialogs
  const [dialogOpen, setDialogOpen] = useState(false);
  // When non-null, the dialog is in EDIT mode (PUT /schedules/{id}); when null, ADD mode (POST loop across day range).
  const [editingScheduleId, setEditingScheduleId] = useState(null);
  const [employeeDialogOpen, setEmployeeDialogOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [vacationDialogOpen, setVacationDialogOpen] = useState(false);
  const [vacationReviewDialogOpen, setVacationReviewDialogOpen] = useState(false);
  
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedVacationRequest, setSelectedVacationRequest] = useState(null);
  
  // Form states
  const [formData, setFormData] = useState({ project_id: '', start_date: '', end_date: '', employee_ids: [] });
  const [employeeFormData, setEmployeeFormData] = useState({
    name: '', email: PLACEHOLDER_EMAIL, password: '', role: 'site_operative', trade: '', rate_per_hour: ''
  });
  const [vacationFormData, setVacationFormData] = useState({
    start_date: format(new Date(), 'yyyy-MM-dd'),
    end_date: format(new Date(), 'yyyy-MM-dd'),
    reason: ''
  });
  const [vacationReviewData, setVacationReviewData] = useState({ status: '', admin_notes: '' });
  const [exportDateRange, setExportDateRange] = useState({
    start_date: format(startOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd'),
    end_date: format(endOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd')
  });
  
  const [submitting, setSubmitting] = useState(false);
  const [employeeFilter, setEmployeeFilter] = useState('all');
  const [projectFilter, setProjectFilter] = useState(() => new URLSearchParams(window.location.search).get('project') || 'all');
  const [draggedSchedule, setDraggedSchedule] = useState(null);
  const [draggedProject, setDraggedProject] = useState(null);
  const [refreshingProjects, setRefreshingProjects] = useState(false);
  
  const { api, canManageProjects, user, isSiteOperative, isAdmin } = useAuth();
  const navigate = useNavigate();

  // Persist viewMode across reloads
  useEffect(() => {
    try { localStorage.setItem('lux_schedule_view', viewMode); } catch {} // eslint-disable-line no-empty
  }, [viewMode]);

  const employeeColorMap = useMemo(() => {
    const map = {};
    employees.forEach((emp, idx) => {
      map[emp.id] = EMPLOYEE_COLORS[idx % EMPLOYEE_COLORS.length];
    });
    return map;
  }, [employees]);

  // Project color map - each project gets a consistent color.
  // Sort by id first so colour assignment stays stable even when the
  // backend returns projects in a different order between fetches.
  const projectColorMap = useMemo(() => {
    const map = {};
    [...projects]
      .sort((a, b) => (a.id || '').localeCompare(b.id || ''))
      .forEach((proj, idx) => {
        map[proj.id] = PROJECT_COLORS[idx % PROJECT_COLORS.length];
      });
    return map;
  }, [projects]);

  // Lightweight projects-only refresher used by:
  //   • manual refresh button on the project sidebar
  //   • 60 s background poll
  //   • window focus event
  // Does NOT touch the schedules / employees state — those refresh on
  // viewMode/currentDate change in fetchData().
  const refreshProjects = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setRefreshingProjects(true);
    try {
      const res = await api.get('/projects?status=Live');
      setProjects(res.data);
    } catch (e) {
      if (!silent) console.error('Failed to refresh projects:', e);
    } finally {
      if (!silent) setRefreshingProjects(false);
    }
  }, [api]);

  // Background project refresh — keep the sidebar live without a hard reload.
  useEffect(() => {
    const interval = setInterval(() => refreshProjects({ silent: true }), 60_000);
    const onFocus = () => refreshProjects({ silent: true });
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, [refreshProjects]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      let startDate, endDate;
      if (viewMode === 'week' || viewMode === 'list') {
        startDate = startOfWeek(currentDate, { weekStartsOn: 1 });
        endDate = endOfWeek(currentDate, { weekStartsOn: 1 });
      } else {
        startDate = startOfMonth(currentDate);
        endDate = endOfMonth(currentDate);
      }

      const [schedulesRes, projectsRes, employeesRes] = await Promise.all([
        api.get(`/schedules?start_date=${format(startDate, 'yyyy-MM-dd')}&end_date=${format(endDate, 'yyyy-MM-dd')}`),
        api.get('/projects?status=Live'),
        api.get('/employees')
      ]);

      setSchedules(schedulesRes.data);
      setProjects(projectsRes.data);
      setEmployees(employeesRes.data);
      api.get('/portal/schedule-options').then(r => setTeam(r.data.team || [])).catch(() => setTeam([]));
    } catch (error) {
      console.error('Failed to fetch schedule data:', error);
      toast.error('Failed to load schedule');
    } finally {
      setLoading(false);
    }
  }, [api, currentDate, viewMode]);

  const fetchVacationRequests = useCallback(async () => {
    try {
      const response = await api.get('/vacation-requests');
      setVacationRequests(response.data);
    } catch (error) {
      console.error('Failed to fetch vacation requests:', error);
    }
  }, [api]);

  const fetchTimeReports = useCallback(async () => {
    setTimeReportsLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('start_date', timeReportFilters.start_date);
      params.append('end_date', timeReportFilters.end_date);
      if (timeReportFilters.employee_id) {
        params.append('employee_id', timeReportFilters.employee_id);
      }
      const response = await api.get(`/admin/time-reports?${params.toString()}`);
      setTimeReports(response.data);
    } catch (error) {
      console.error('Failed to fetch time reports:', error);
      toast.error('Failed to load time reports');
    } finally {
      setTimeReportsLoading(false);
    }
  }, [api, timeReportFilters]);

  const fetchTimeReportEmployees = useCallback(async () => {
    try {
      const response = await api.get('/admin/time-reports/employees');
      setTimeReportEmployees(response.data);
    } catch (error) {
      console.error('Failed to fetch employees for time reports:', error);
    }
  }, [api]);

  // useEffects must come AFTER the useCallback declarations above —
  // `const fn = useCallback(...)` bindings are in the TDZ until their line
  // is executed, and React evaluates the dep array during render top-to-bottom.
  useEffect(() => {
    fetchData();
  }, [currentDate, viewMode, fetchData]);

  useEffect(() => {
    if (activeTab === 'vacation') {
      fetchVacationRequests();
    } else if (activeTab === 'time-reports') {
      fetchTimeReports();
      fetchTimeReportEmployees();
    }
  }, [activeTab, fetchVacationRequests, fetchTimeReports, fetchTimeReportEmployees]);

  const handleDeleteTimeEntry = async (entry) => {
    if (!confirm('Delete this time entry?')) return;
    try {
      await api.delete(entry.kind === 'hours' ? `/admin/hours/${entry.id}` : `/admin/time-entries/${entry.id}`);
      toast.success('Time entry deleted');
      fetchTimeReports();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete time entry');
    }
  };

  const handleTimeReportFilterChange = (key, value) => {
    setTimeReportFilters(prev => ({ ...prev, [key]: value }));
  };

  const applyTimeReportFilters = () => {
    fetchTimeReports();
  };

  const days = useMemo(() => {
    if (viewMode === 'week' || viewMode === 'list') {
      const start = startOfWeek(currentDate, { weekStartsOn: 1 });
      const end = endOfWeek(currentDate, { weekStartsOn: 1 });
      return eachDayOfInterval({ start, end });
    } else {
      const start = startOfMonth(currentDate);
      const end = endOfMonth(currentDate);
      const monthStart = startOfWeek(start, { weekStartsOn: 1 });
      const monthEnd = endOfWeek(end, { weekStartsOn: 1 });
      return eachDayOfInterval({ start: monthStart, end: monthEnd });
    }
  }, [currentDate, viewMode]);

  // Weekly flags (week/list views): no job this week for the admin's team (or
  // every field worker when no team is set) + same person on two different jobs.
  const flags = useMemo(() => {
    const watch = team.length ? team : employees.map(e => ({ id: e.id, name: e.name }));
    return computeScheduleFlags(flattenSchedules(schedules), watch);
  }, [schedules, team, employees]);

  const getSchedulesForDay = useCallback((date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    let filtered = schedules.filter(s => s.date === dateStr);
    
    if (employeeFilter !== 'all') {
      filtered = filtered.filter(s => s.employees.some(e => e.id === employeeFilter));
    }
    
    if (projectFilter !== 'all') {
      filtered = filtered.filter(s => s.project_id === projectFilter);
    }
    
    return filtered;
  }, [schedules, employeeFilter, projectFilter]);

  const navigatePrev = () => {
    if (viewMode === 'week' || viewMode === 'list') setCurrentDate(subWeeks(currentDate, 1));
    else setCurrentDate(subMonths(currentDate, 1));
  };

  const navigateNext = () => {
    if (viewMode === 'week' || viewMode === 'list') setCurrentDate(addWeeks(currentDate, 1));
    else setCurrentDate(addMonths(currentDate, 1));
  };

  const goToToday = () => setCurrentDate(new Date());

  // openAddDialog accepts:
  //   (date)                           → blank form
  //   (date, scheduleToDuplicate)      → copy project + crew from an existing shift
  //   (date, prefilledEmployeeId)      → pre-check a single employee (from ListView '+' click)
  const openAddDialog = (date, prefill = null) => {
    if (!canManageProjects) {
      toast.error('Only Admin and Office users can schedule jobs');
      return;
    }
    setSelectedDate(date);
    const dateStr = format(date, 'yyyy-MM-dd');
    if (prefill && typeof prefill === 'object' && Array.isArray(prefill.employees)) {
      // Duplicate path — full schedule object passed in
      setFormData({
        project_id: prefill.project_id,
        start_date: dateStr,
        end_date: dateStr,
        employee_ids: prefill.employees.map(e => e.id)
      });
    } else if (typeof prefill === 'string') {
      // ListView '+' click — single employee ID
      setFormData({ project_id: '', start_date: dateStr, end_date: dateStr, employee_ids: [prefill] });
    } else {
      setFormData({ project_id: '', start_date: dateStr, end_date: dateStr, employee_ids: [] });
    }
    setDialogOpen(true);
  };

  const handleDuplicate = (schedule, targetDate) => openAddDialog(targetDate, schedule);

  // Double-click on a shift card opens the dialog in EDIT mode.
  // The dialog UI is reused 1:1; submit branches on editingScheduleId.
  const openEditDialog = (schedule) => {
    if (!canManageProjects) {
      toast.error('Only Admin and Office users can edit shifts');
      return;
    }
    setEditingScheduleId(schedule.id);
    setSelectedDate(parseISO(schedule.date));
    setFormData({
      project_id: schedule.project_id,
      start_date: schedule.date,
      end_date: schedule.date,
      employee_ids: (schedule.employees || []).map(e => e.id),
    });
    setDialogOpen(true);
  };

  // Reset edit state whenever the dialog closes so the next open starts fresh.
  useEffect(() => {
    if (!dialogOpen) setEditingScheduleId(null);
  }, [dialogOpen]);

  const handleEmployeeToggle = (employeeId) => {
    setFormData(prev => ({
      ...prev,
      employee_ids: prev.employee_ids.includes(employeeId)
        ? prev.employee_ids.filter(id => id !== employeeId)
        : [...prev.employee_ids, employeeId]
    }));
  };

  const handleSubmit = async () => {
    if (!formData.project_id) { toast.error('Please select a project'); return; }
    if (formData.employee_ids.length === 0) { toast.error('Please select at least one employee'); return; }
    if (!formData.start_date || !formData.end_date) { toast.error('Please select dates'); return; }

    // EDIT mode — single PUT, ignore end_date (edit only acts on the shift's own date).
    if (editingScheduleId) {
      setSubmitting(true);
      try {
        await api.put(`/schedules/${editingScheduleId}`, {
          project_id: formData.project_id,
          date: formData.start_date,
          employee_ids: formData.employee_ids,
        });
        toast.success('Shift updated');
        setDialogOpen(false);
        fetchData();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to update shift');
      } finally {
        setSubmitting(false);
      }
      return;
    }

    // ADD mode — loop across start..end date range creating one shift per day.
    const startDate = parseISO(formData.start_date);
    const endDate = parseISO(formData.end_date);
    
    if (endDate < startDate) {
      toast.error('End date cannot be before start date');
      return;
    }

    const daysToSchedule = eachDayOfInterval({ start: startDate, end: endDate });
    const totalDays = daysToSchedule.length;

    setSubmitting(true);
    try {
      // Create a schedule for each day in the range
      let successCount = 0;
      for (const day of daysToSchedule) {
        await api.post('/schedules', {
          project_id: formData.project_id,
          date: format(day, 'yyyy-MM-dd'),
          employee_ids: formData.employee_ids
        });
        successCount++;
      }
      
      if (totalDays === 1) {
        toast.success('Job scheduled successfully');
      } else {
        toast.success(`${successCount} shifts created (${format(startDate, 'dd MMM')} - ${format(endDate, 'dd MMM')})`);
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to schedule job');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (scheduleId, e) => {
    e.stopPropagation();
    if (!confirm('Delete this scheduled job?')) return;
    try {
      await api.delete(`/schedules/${scheduleId}`);
      toast.success('Schedule deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete schedule');
    }
  };

  // Employee CRUD
  const openEmployeeDialog = () => {
    setEmployeeFormData({ name: '', email: PLACEHOLDER_EMAIL, password: '', role: 'site_operative', trade: '', rate_per_hour: '' });
    setEmployeeDialogOpen(true);
  };

  const handleCreateEmployee = async () => {
    if (!employeeFormData.name.trim()) { toast.error('Please enter employee name'); return; }
    if (!employeeFormData.email.trim()) { toast.error('Please enter employee email'); return; }
    if (!employeeFormData.password.trim() || employeeFormData.password.length < 6) {
      toast.error('Password must be at least 6 characters'); return;
    }

    setSubmitting(true);
    try {
      await api.post('/employees', {
        ...employeeFormData,
        rate_per_hour: employeeFormData.rate_per_hour ? parseFloat(employeeFormData.rate_per_hour) : null
      });
      toast.success('Employee added successfully');
      setEmployeeDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add employee');
    } finally {
      setSubmitting(false);
    }
  };

  // Vacation Requests
  const openVacationDialog = () => {
    setVacationFormData({
      start_date: format(new Date(), 'yyyy-MM-dd'),
      end_date: format(new Date(), 'yyyy-MM-dd'),
      reason: ''
    });
    setVacationDialogOpen(true);
  };

  const handleCreateVacationRequest = async () => {
    if (vacationFormData.start_date > vacationFormData.end_date) {
      toast.error('Start date must be before end date'); return;
    }

    setSubmitting(true);
    try {
      await api.post('/vacation-requests', vacationFormData);
      toast.success('Vacation request submitted');
      setVacationDialogOpen(false);
      fetchVacationRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const openVacationReviewDialog = (request) => {
    setSelectedVacationRequest(request);
    setVacationReviewData({ status: request.status, admin_notes: request.admin_notes || '' });
    setVacationReviewDialogOpen(true);
  };

  const handleReviewVacation = async () => {
    if (!vacationReviewData.status) { toast.error('Please select a status'); return; }

    setSubmitting(true);
    try {
      await api.put(`/vacation-requests/${selectedVacationRequest.id}`, vacationReviewData);
      toast.success('Vacation request updated');
      setVacationReviewDialogOpen(false);
      fetchVacationRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteVacationRequest = async (requestId) => {
    if (!confirm('Delete this vacation request?')) return;
    try {
      await api.delete(`/vacation-requests/${requestId}`);
      toast.success('Request deleted');
      fetchVacationRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete request');
    }
  };

  // Drag and Drop
  const handleDragStart = (e, schedule) => {
    if (!canManageProjects) return;
    setDraggedSchedule(schedule);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };

  // handleDrop supports two flavours:
  //   week/month view  → only `targetDate` provided (project + crew unchanged)
  //   list view        → `targetEmployeeId` also provided (REASSIGNS the
  //                       shift to that single employee, project unchanged).
  //                       Pass `null` to drop on the Unassigned row.
  const handleDrop = async (e, targetDate, targetEmployeeId = undefined) => {
    e.preventDefault();
    if (!draggedSchedule || !canManageProjects) return;
    const newDateStr = format(targetDate, 'yyyy-MM-dd');

    // List-view drop: build the payload that changes BOTH date and crew.
    if (targetEmployeeId !== undefined) {
      const current = (draggedSchedule.employees || []).map(emp => emp.id);
      const nextCrew = targetEmployeeId === null ? [] : [targetEmployeeId];
      // No-op guard: same day + same single-employee crew → skip the network round-trip.
      const sameDate = draggedSchedule.date === newDateStr;
      const sameCrew = current.length === nextCrew.length && current.every(id => nextCrew.includes(id));
      if (sameDate && sameCrew) { setDraggedSchedule(null); return; }
      try {
        await api.put(`/schedules/${draggedSchedule.id}`, {
          date: newDateStr,
          employee_ids: nextCrew,
        });
        toast.success(targetEmployeeId === null ? 'Shift moved (unassigned)' : 'Shift moved & reassigned');
        fetchData();
      } catch (error) {
        toast.error(error?.response?.data?.detail || 'Failed to move shift');
      } finally {
        setDraggedSchedule(null);
      }
      return;
    }

    // Week/month-view drop: date only.
    if (draggedSchedule.date === newDateStr) { setDraggedSchedule(null); return; }
    try {
      await api.put(`/schedules/${draggedSchedule.id}`, { date: newDateStr });
      toast.success('Schedule moved');
      fetchData();
    } catch (error) {
      toast.error('Failed to move schedule');
    } finally {
      setDraggedSchedule(null);
    }
  };

  const handleDragEnd = () => setDraggedSchedule(null);

  // Project-from-sidebar drag → create a new shift on the target cell.
  // Cell-aware: in list view targetEmployeeId is the employee id (or null
  // for the Unassigned row). In week/month views it's undefined and the
  // shift is created with no crew (admin can assign people later).
  const handleProjectDrop = async (targetDate, targetEmployeeId) => {
    if (!draggedProject || !canManageProjects) return;
    const dateStr = format(targetDate, 'yyyy-MM-dd');
    const crew = targetEmployeeId ? [targetEmployeeId] : [];
    try {
      await api.post('/schedules', {
        project_id: draggedProject.id,
        date: dateStr,
        employee_ids: crew,
      });
      toast.success(crew.length ? 'Shift created' : 'Shift created (unassigned)');
      fetchData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to create shift');
    } finally {
      setDraggedProject(null);
    }
  };

  // Unified cell-drop handler — the views call this; it routes to the
  // correct underlying action depending on what is currently being dragged.
  const handleCellDrop = (e, targetDate, targetEmployeeId) => {
    e.preventDefault();
    if (draggedProject) {
      return handleProjectDrop(targetDate, targetEmployeeId);
    }
    return handleDrop(e, targetDate, targetEmployeeId);
  };

  // Export
  const openExportDialog = () => {
    let startDate, endDate;
    if (viewMode === 'week' || viewMode === 'list') {
      startDate = startOfWeek(currentDate, { weekStartsOn: 1 });
      endDate = endOfWeek(currentDate, { weekStartsOn: 1 });
    } else {
      startDate = startOfMonth(currentDate);
      endDate = endOfMonth(currentDate);
    }
    setExportDateRange({ start_date: format(startDate, 'yyyy-MM-dd'), end_date: format(endDate, 'yyyy-MM-dd') });
    setExportDialogOpen(true);
  };

  const exportToCSV = async () => {
    try {
      const response = await api.get(`/schedules?start_date=${exportDateRange.start_date}&end_date=${exportDateRange.end_date}`);
      let exportData = response.data;
      if (employeeFilter !== 'all') exportData = exportData.filter(s => s.employees.some(e => e.id === employeeFilter));
      if (projectFilter !== 'all') exportData = exportData.filter(s => s.project_id === projectFilter);

      const headers = ['Date', 'Day', 'Project Reference', 'Project Address', 'Employee Name', 'Trade', 'Rate/Hour (£)', 'Role'];
      const rows = [];
      exportData.forEach(s => {
        const projectParts = s.project_ref?.split(' - ') || ['', ''];
        // If employee filter is active, only include that employee's data
        // Otherwise include all employees on the shift
        const employeesToExport = employeeFilter !== 'all' 
          ? s.employees.filter(e => e.id === employeeFilter)
          : s.employees;
        employeesToExport.forEach(emp => {
          rows.push([s.date, format(parseISO(s.date), 'EEEE'), projectParts[0], projectParts[1] || '',
            emp.name, emp.trade || '', emp.rate_per_hour ? emp.rate_per_hour.toFixed(2) : '', emp.role.replace('_', ' ')]);
        });
      });

      const csvContent = [headers.join(','), ...rows.map(row => row.map(cell => `"${cell}"`).join(','))].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `lux-schedule-${exportDateRange.start_date}-to-${exportDateRange.end_date}.csv`;
      link.click();
      toast.success('Schedule exported');
      setExportDialogOpen(false);
    } catch (error) {
      toast.error('Failed to export');
    }
  };

  const getHeaderTitle = () => {
    if (viewMode === 'week' || viewMode === 'list') {
      const start = startOfWeek(currentDate, { weekStartsOn: 1 });
      const end = endOfWeek(currentDate, { weekStartsOn: 1 });
      return `${format(start, 'dd MMM')} - ${format(end, 'dd MMM yyyy')}`;
    }
    return format(currentDate, 'MMMM yyyy');
  };

  const getInitials = (name) => name.split(' ').map(n => n[0]).join('').toUpperCase();

  // Unique projects from schedules for filter (sorted by prefix + number for consistency with the right rail).
  const scheduledProjects = useMemo(() => {
    const projectIds = new Set(schedules.map(s => s.project_id));
    return projects.filter(p => projectIds.has(p.id)).sort(compareProjectRefs);
  }, [schedules, projects]);

  // Same sort applied to the full live-project list (used in the Add Shift dialog dropdown).
  const sortedProjects = useMemo(() => [...projects].sort(compareProjectRefs), [projects]);

  return (
    <div className="container mx-auto px-4 py-6 md:py-8 max-w-7xl">
      <BackToDiary className="mb-4" />
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            Job Schedule
          </h1>
          <p className="text-gray-400 mt-1">
            {isSiteOperative ? 'View your shifts and team members' : 'Assign employees to daily projects'}
          </p>
        </div>

        {/* Tabs */}
        <div className="flex bg-[#1E2530] rounded-md p-1">
          <Button
            variant={activeTab === 'schedule' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setActiveTab('schedule')}
            className={activeTab === 'schedule' ? 'bg-[#F5A623] text-[#1E2530]' : 'text-gray-400'}
          >
            <CalendarIcon className="w-4 h-4 mr-1" />
            Schedule
          </Button>
          <Button
            variant={activeTab === 'vacation' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setActiveTab('vacation')}
            data-testid="vacation-tab"
            className={activeTab === 'vacation' ? 'bg-[#F5A623] text-[#1E2530]' : 'text-gray-400'}
          >
            <Palmtree className="w-4 h-4 mr-1" />
            Vacation
          </Button>
          {canManageProjects && (
            <Button
              variant={activeTab === 'time-reports' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setActiveTab('time-reports')}
              data-testid="time-reports-tab"
              className={activeTab === 'time-reports' ? 'bg-[#F5A623] text-[#1E2530]' : 'text-gray-400'}
            >
              <Clock className="w-4 h-4 mr-1" />
              Time Reports
            </Button>
          )}
          {canManageProjects && (
            <Button
              variant={activeTab === 'reconcile' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setActiveTab('reconcile')}
              data-testid="reconcile-tab"
              className={activeTab === 'reconcile' ? 'bg-[#F5A623] text-[#1E2530]' : 'text-gray-400'}
            >
              <LayoutList className="w-4 h-4 mr-1" />
              Plan vs Actual
            </Button>
          )}
        </div>
      </div>

      {activeTab === 'reconcile' && canManageProjects && (
        <div className="space-y-4" data-testid="desktop-reconcile">
          <div className="flex items-center justify-between bg-[#2A3240] border border-white/10 rounded-lg p-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setReconcileWeek(w => subWeeks(w, 1))}
              className="text-gray-400 hover:text-white"
              data-testid="reconcile-prev-week"
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <span className="text-white font-semibold">
              {format(reconcileWeek, 'd MMM')} – {format(endOfWeek(reconcileWeek, { weekStartsOn: 1 }), 'd MMM yyyy')}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setReconcileWeek(w => addWeeks(w, 1))}
              className="text-gray-400 hover:text-white"
              data-testid="reconcile-next-week"
            >
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
          <ReconcilePanel
            api={api}
            weekStart={reconcileWeek}
            onOpenSchedule={(r) => {
              setCurrentDate(new Date(r.date + 'T00:00:00'));
              setViewMode('week');
              setActiveTab('schedule');
            }}
            onLogInDiary={(r) => {
              const pid = (r.scheduled[0] || r.logged[0] || {}).project_id;
              if (!pid) return;
              navigate(`/diary/${pid}`, { state: { seedDate: r.date, labourSeed: { people: [{ source: r.user_type === 'sub_contractor' ? 'subcontractor' : 'employee', id: r.person_id, name: r.person_name }] } } });
            }}
          />
        </div>
      )}

      {activeTab === 'schedule' && (
        <>
          {/* Schedule Controls */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            {canManageProjects && (
              <Button variant="outline" size="sm" onClick={openEmployeeDialog} data-testid="add-employee-btn"
                className="border-white/20 text-gray-300 hover:bg-white/5">
                <UserPlus className="w-4 h-4 mr-2" />Add Employee
              </Button>
            )}

            {/* Project Filter */}
            <Select value={projectFilter} onValueChange={setProjectFilter}>
              <SelectTrigger className="w-[200px] bg-[#1E2530] border-white/20 text-white" data-testid="project-filter-select">
                <FolderKanban className="w-4 h-4 mr-2 text-gray-500" />
                <SelectValue placeholder="All Projects" />
              </SelectTrigger>
              <SelectContent className="bg-[#2A3240] border-white/10 max-h-[300px]">
                <SelectItem value="all" className="text-white focus:bg-[#F5A623]/20">All Projects</SelectItem>
                {scheduledProjects.map(proj => (
                  <SelectItem key={proj.id} value={proj.id} className="text-white focus:bg-[#F5A623]/20">
                    {proj.project_ref?.split(' - ')[0]} - {proj.project_ref?.split(' - ')[1]?.substring(0, 20)}...
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Employee Filter */}
            <Select value={employeeFilter} onValueChange={setEmployeeFilter}>
              <SelectTrigger className="w-[180px] bg-[#1E2530] border-white/20 text-white" data-testid="employee-filter-select">
                <Filter className="w-4 h-4 mr-2 text-gray-500" />
                <SelectValue placeholder="All Employees" />
              </SelectTrigger>
              <SelectContent className="bg-[#2A3240] border-white/10">
                <SelectItem value="all" className="text-white focus:bg-[#F5A623]/20">All Employees</SelectItem>
                {employees.map(emp => (
                  <SelectItem key={emp.id} value={emp.id} className="text-white focus:bg-[#F5A623]/20">
                    <div className="flex items-center gap-2">
                      <div className={`w-3 h-3 rounded-full ${employeeColorMap[emp.id]?.bg} border ${employeeColorMap[emp.id]?.border}`} />
                      {emp.name} {emp.trade && <span className="text-gray-500 text-xs">({emp.trade})</span>}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {canManageProjects && (
              <Button variant="outline" size="sm" onClick={openExportDialog} data-testid="export-csv-btn"
                className="border-white/20 text-gray-300 hover:bg-white/5">
                <Download className="w-4 h-4 mr-2" />Export CSV
              </Button>
            )}

            {isAdmin && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/schedule-changes')}
                data-testid="view-schedule-changes-btn"
                className="border-white/20 text-gray-300 hover:bg-white/5"
              >
                <ClipboardList className="w-4 h-4 mr-2" />View Schedule Changes
              </Button>
            )}

            {/* View Toggle — desktop has 3 buttons; mobile sees only Week + Month (list view is desktop-only) */}
            <div className="flex bg-[#1E2530] rounded-md p-1 ml-auto">
              <Button variant={viewMode === 'list' ? 'default' : 'ghost'} size="sm" onClick={() => setViewMode('list')}
                data-testid="view-list-btn"
                className={`hidden md:inline-flex ${viewMode === 'list' ? 'bg-[#F5A623] text-[#1E2530]' : 'text-gray-400'}`}>
                <LayoutList className="w-4 h-4 mr-1" />List
              </Button>
              <Button variant={viewMode === 'week' ? 'default' : 'ghost'} size="sm" onClick={() => setViewMode('week')}
                data-testid="view-week-btn"
                className={viewMode === 'week' ? 'bg-[#F5A623] text-[#1E2530]' : 'text-gray-400'}>
                <LayoutList className="w-4 h-4 mr-1" />Week
              </Button>
              <Button variant={viewMode === 'month' ? 'default' : 'ghost'} size="sm" onClick={() => setViewMode('month')}
                data-testid="view-month-btn"
                className={viewMode === 'month' ? 'bg-[#F5A623] text-[#1E2530]' : 'text-gray-400'}>
                <Grid3X3 className="w-4 h-4 mr-1" />Month
              </Button>
            </div>
          </div>

          {/* Employee Legend */}
          <div className="flex flex-wrap items-center gap-3 mb-4 p-3 bg-[#2A3240] rounded-lg border border-white/10">
            <span className="text-xs text-gray-500 uppercase font-semibold">Employees:</span>
            {employees.map(emp => (
              <div key={emp.id} className="flex items-center gap-1.5"
                title={`${emp.name}${emp.trade ? ` - ${emp.trade}` : ''}${emp.rate_per_hour ? ` - £${emp.rate_per_hour}/hr` : ''}`}>
                <div className={`w-3 h-3 rounded-full ${employeeColorMap[emp.id]?.bg} border ${employeeColorMap[emp.id]?.border}`} />
                <span className={`text-xs ${employeeColorMap[emp.id]?.text}`}>{emp.name}</span>
                {emp.trade && <span className="text-[10px] text-gray-500">({emp.trade})</span>}
              </div>
            ))}
          </div>

          {/* Calendar Navigation */}
          <div className="flex items-center justify-between mb-6 bg-[#2A3240] rounded-lg p-4">
            <Button variant="ghost" size="sm" onClick={navigatePrev} className="text-gray-400 hover:text-white">
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-4">
              <h2 className="text-xl md:text-2xl font-semibold text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                {getHeaderTitle()}
              </h2>
              <Button variant="outline" size="sm" onClick={goToToday} className="border-white/20 text-gray-300 hover:bg-white/5">
                Today
              </Button>
            </div>
            <Button variant="ghost" size="sm" onClick={navigateNext} className="text-gray-400 hover:text-white">
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>

          {/* Calendar Views + Right-rail project filter sidebar */}
          {loading ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#F5A623]" /></div>
          ) : (
            <div className="flex gap-4 items-start">
              <div className="flex-1 min-w-0">
                {viewMode !== 'month' && canManageProjects && (
                  <ScheduleFlagsCard
                    flags={flags}
                    teamLabel={team.length ? 'Your contractors' : 'Field staff'}
                    onAddFor={(pid) => openAddDialog(days.find(d => isToday(d)) || days[0], pid)}
                  />
                )}
                {viewMode === 'list' && (
                  <ScheduleListView
                    days={days}
                    schedules={schedules}
                    employees={employees}
                    canManageProjects={canManageProjects}
                    projectColorMap={projectColorMap}
                    employeeColorMap={employeeColorMap}
                    PROJECT_COLORS={PROJECT_COLORS}
                    EMPLOYEE_COLORS={EMPLOYEE_COLORS}
                    draggedSchedule={draggedSchedule}
                    draggedProject={draggedProject}
                    getSchedulesForDay={getSchedulesForDay}
                    getInitials={getInitials}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    onDragOver={handleDragOver}
                    onDrop={handleCellDrop}
                    onDuplicate={handleDuplicate}
                    onDelete={handleDelete}
                    onAddClick={openAddDialog}
                    onEdit={openEditDialog}
                  />
                )}

                {viewMode === 'week' && (
                  <ScheduleWeekView
                    days={days}
                    schedules={schedules}
                    canManageProjects={canManageProjects}
                    projectColorMap={projectColorMap}
                    employeeColorMap={employeeColorMap}
                    PROJECT_COLORS={PROJECT_COLORS}
                    EMPLOYEE_COLORS={EMPLOYEE_COLORS}
                    draggedSchedule={draggedSchedule}
                    draggedProject={draggedProject}
                    getSchedulesForDay={getSchedulesForDay}
                    getInitials={getInitials}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    onDragOver={handleDragOver}
                    onDrop={handleCellDrop}
                    onDuplicate={handleDuplicate}
                    onDelete={handleDelete}
                    onAddClick={openAddDialog}
                    onEdit={openEditDialog}
                    dupKeys={flags.dupKeys}
                  />
                )}

                {viewMode === 'month' && (
                  <ScheduleMonthView
                    days={days}
                    currentDate={currentDate}
                    canManageProjects={canManageProjects}
                    projectColorMap={projectColorMap}
                    employeeColorMap={employeeColorMap}
                    PROJECT_COLORS={PROJECT_COLORS}
                    EMPLOYEE_COLORS={EMPLOYEE_COLORS}
                    draggedSchedule={draggedSchedule}
                    draggedProject={draggedProject}
                    getSchedulesForDay={getSchedulesForDay}
                    getInitials={getInitials}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    onDragOver={handleDragOver}
                    onDrop={handleCellDrop}
                    onDuplicate={handleDuplicate}
                    onDelete={handleDelete}
                    onAddClick={openAddDialog}
                    onEdit={openEditDialog}
                  />
                )}
              </div>

              <ProjectFilterSidebar
                projects={projects}
                schedules={schedules}
                projectColorMap={projectColorMap}
                projectFilter={projectFilter}
                onSelectProject={setProjectFilter}
                onRefresh={() => refreshProjects()}
                refreshing={refreshingProjects}
                /* Drag-to-create only in List view — Week/Month would leave
                   shifts unassigned which is more confusing than useful. */
                canDragProjects={canManageProjects && viewMode === 'list'}
                onProjectDragStart={setDraggedProject}
                onProjectDragEnd={() => setDraggedProject(null)}
              />
            </div>
          )}
        </>
      )}

      {/* Vacation Tab */}
      {activeTab === 'vacation' && (
        <VacationTab
          vacationRequests={vacationRequests}
          canManageProjects={canManageProjects}
          user={user}
          onRequestVacation={openVacationDialog}
          onReviewRequest={openVacationReviewDialog}
          onDeleteRequest={handleDeleteVacationRequest}
        />
      )}

      {/* Time Reports Tab */}
      {activeTab === 'time-reports' && canManageProjects && (
        <TimeReportsTab
          api={api}
          employees={employees}
          projects={projects}
          onRefresh={fetchTimeReports}
          timeReportFilters={timeReportFilters}
          timeReportEmployees={timeReportEmployees}
          timeReports={timeReports}
          timeReportsLoading={timeReportsLoading}
          onFilterChange={handleTimeReportFilterChange}
          onApplyFilters={applyTimeReportFilters}
          onDeleteEntry={handleDeleteTimeEntry}
        />
      )}

      {/* Schedule Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#2A3240] border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">{editingScheduleId ? 'Edit Shift' : 'Schedule Job'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            {/* Date Range (or single date in edit mode) */}
            <div className={`grid gap-3 ${editingScheduleId ? 'grid-cols-1' : 'grid-cols-2'}`}>
              <div className="space-y-2">
                <Label className="text-gray-300 flex items-center gap-2">
                  <CalendarIcon className="w-4 h-4" />
                  {editingScheduleId ? 'Date' : 'Start Date'}
                </Label>
                <Input 
                  type="date" 
                  value={formData.start_date} 
                  onChange={(e) => setFormData(p => ({ ...p, start_date: e.target.value, end_date: p.end_date < e.target.value ? e.target.value : p.end_date }))}
                  className="bg-[#1E2530] border-white/20 text-white"
                />
              </div>
              {!editingScheduleId && (
                <div className="space-y-2">
                  <Label className="text-gray-300 flex items-center gap-2">
                    <CalendarIcon className="w-4 h-4" />
                    End Date
                  </Label>
                  <Input 
                    type="date" 
                    value={formData.end_date} 
                    min={formData.start_date}
                    onChange={(e) => setFormData(p => ({ ...p, end_date: e.target.value }))}
                    className="bg-[#1E2530] border-white/20 text-white"
                  />
                </div>
              )}
            </div>
            {!editingScheduleId && formData.start_date && formData.end_date && formData.start_date !== formData.end_date && (
              <div className="text-xs text-amber-400 bg-amber-500/10 p-2 rounded border border-amber-500/30">
                This will create {differenceInDays(parseISO(formData.end_date), parseISO(formData.start_date)) + 1} shifts ({format(parseISO(formData.start_date), 'dd MMM')} - {format(parseISO(formData.end_date), 'dd MMM')})
              </div>
            )}
            
            <div className="space-y-2">
              <Label className="text-gray-300">Project</Label>
              <Select value={formData.project_id} onValueChange={(v) => setFormData(p => ({ ...p, project_id: v }))}>
                <SelectTrigger className="bg-[#1E2530] border-white/20 text-white"><SelectValue placeholder="Select project" /></SelectTrigger>
                <SelectContent className="bg-[#2A3240] border-white/10 max-h-[200px]">
                  {sortedProjects.map(p => <SelectItem key={p.id} value={p.id} className="text-white focus:bg-[#F5A623]/20">{p.project_ref}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Assign Employees</Label>
              <div className="max-h-[200px] overflow-y-auto space-y-2 p-3 bg-[#1E2530] rounded-md border border-white/10">
                {employees.map(emp => {
                  const empColor = employeeColorMap[emp.id] || EMPLOYEE_COLORS[0];
                  return (
                    <label key={emp.id} className={`flex items-center gap-3 p-2 rounded cursor-pointer ${formData.employee_ids.includes(emp.id) ? `${empColor.bg} border ${empColor.border}` : 'hover:bg-white/5'}`}>
                      <Checkbox checked={formData.employee_ids.includes(emp.id)} onCheckedChange={() => handleEmployeeToggle(emp.id)}
                        className="border-white/30 data-[state=checked]:bg-[#F5A623]" />
                      <div className="flex items-center gap-2 flex-1">
                        <div className={`w-3 h-3 rounded-full ${empColor.bg} border ${empColor.border}`} />
                        <div className="flex-1">
                          <p className="text-white text-sm">{emp.name}</p>
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            <span className="capitalize">{emp.role.replace('_', ' ')}</span>
                            {emp.trade && <><span>•</span><span>{emp.trade}</span></>}
                          </div>
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={() => setDialogOpen(false)} className="flex-1 border-white/20 text-gray-300">Cancel</Button>
              <Button onClick={handleSubmit} disabled={submitting} className="flex-1 bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D]">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : editingScheduleId ? 'Save Changes' : formData.start_date !== formData.end_date ? 'Create Shifts' : 'Schedule'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Employee Dialog */}
      <Dialog open={employeeDialogOpen} onOpenChange={setEmployeeDialogOpen}>
        <DialogContent className="bg-[#2A3240] border-white/10 max-w-md">
          <DialogHeader><DialogTitle className="text-white flex items-center gap-2"><UserPlus className="w-5 h-5 text-[#F5A623]" />Add New Employee</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2"><Label className="text-gray-300">Name *</Label>
              <Input value={employeeFormData.name} onChange={(e) => setEmployeeFormData(p => ({ ...p, name: e.target.value }))}
                placeholder="John Smith" className="bg-[#1E2530] border-white/20 text-white" /></div>
            <div className="space-y-2"><Label className="text-gray-300">Email *</Label>
              <Input type="email" value={employeeFormData.email} onChange={(e) => setEmployeeFormData(p => ({ ...p, email: e.target.value }))}
                placeholder="john@luxdev.co.uk" className="bg-[#1E2530] border-white/20 text-white" /></div>
            <div className="space-y-2"><Label className="text-gray-300">Password *</Label>
              <Input type="password" value={employeeFormData.password} onChange={(e) => setEmployeeFormData(p => ({ ...p, password: e.target.value }))}
                placeholder="Min 6 characters" className="bg-[#1E2530] border-white/20 text-white" /></div>
            <div className="space-y-2"><Label className="text-gray-300">Role</Label>
              <Select value={employeeFormData.role} onValueChange={(v) => setEmployeeFormData(p => ({ ...p, role: v }))}>
                <SelectTrigger className="bg-[#1E2530] border-white/20 text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#2A3240] border-white/10">
                  {ROLE_OPTIONS.map(r => <SelectItem key={r.value} value={r.value} className="text-white focus:bg-[#F5A623]/20">{r.label}</SelectItem>)}
                </SelectContent>
              </Select></div>
            <div className="space-y-2"><Label className="text-gray-300 flex items-center gap-2"><Briefcase className="w-4 h-4" />Trade</Label>
              <Select value={employeeFormData.trade} onValueChange={(v) => setEmployeeFormData(p => ({ ...p, trade: v }))}>
                <SelectTrigger className="bg-[#1E2530] border-white/20 text-white"><SelectValue placeholder="Select trade" /></SelectTrigger>
                <SelectContent className="bg-[#2A3240] border-white/10">
                  {TRADE_OPTIONS.map(t => <SelectItem key={t} value={t} className="text-white focus:bg-[#F5A623]/20">{t}</SelectItem>)}
                </SelectContent>
              </Select></div>
            <div className="space-y-2"><Label className="text-gray-300 flex items-center gap-2"><PoundSterling className="w-4 h-4" />Rate per Hour (£)</Label>
              <Input type="number" step="0.01" value={employeeFormData.rate_per_hour} onChange={(e) => setEmployeeFormData(p => ({ ...p, rate_per_hour: e.target.value }))}
                placeholder="25.00" className="bg-[#1E2530] border-white/20 text-white" /></div>
            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={() => setEmployeeDialogOpen(false)} className="flex-1 border-white/20 text-gray-300">Cancel</Button>
              <Button onClick={handleCreateEmployee} disabled={submitting} className="flex-1 bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D]">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add Employee'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Vacation Request Dialog */}
      <Dialog open={vacationDialogOpen} onOpenChange={setVacationDialogOpen}>
        <DialogContent className="bg-[#2A3240] border-white/10 max-w-md">
          <DialogHeader><DialogTitle className="text-white flex items-center gap-2"><Palmtree className="w-5 h-5 text-[#F5A623]" />Request Vacation</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label className="text-gray-300">Start Date</Label>
                <Input type="date" value={vacationFormData.start_date} onChange={(e) => setVacationFormData(p => ({ ...p, start_date: e.target.value }))}
                  className="bg-[#1E2530] border-white/20 text-white" /></div>
              <div className="space-y-2"><Label className="text-gray-300">End Date</Label>
                <Input type="date" value={vacationFormData.end_date} onChange={(e) => setVacationFormData(p => ({ ...p, end_date: e.target.value }))}
                  className="bg-[#1E2530] border-white/20 text-white" /></div>
            </div>
            <div className="space-y-2"><Label className="text-gray-300">Reason (Optional)</Label>
              <Textarea value={vacationFormData.reason} onChange={(e) => setVacationFormData(p => ({ ...p, reason: e.target.value }))}
                placeholder="Family holiday, medical appointment, etc." rows={3} className="bg-[#1E2530] border-white/20 text-white resize-none" /></div>
            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={() => setVacationDialogOpen(false)} className="flex-1 border-white/20 text-gray-300">Cancel</Button>
              <Button onClick={handleCreateVacationRequest} disabled={submitting} data-testid="submit-vacation-btn"
                className="flex-1 bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D]">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Submit Request'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Vacation Review Dialog (Admin/Office) */}
      <Dialog open={vacationReviewDialogOpen} onOpenChange={setVacationReviewDialogOpen}>
        <DialogContent className="bg-[#2A3240] border-white/10 max-w-md">
          <DialogHeader><DialogTitle className="text-white">Review Vacation Request</DialogTitle></DialogHeader>
          {selectedVacationRequest && (
            <div className="space-y-4 pt-4">
              <div className="p-3 bg-[#1E2530] rounded-md">
                <p className="font-semibold text-white">{selectedVacationRequest.employee_name}</p>
                <p className="text-sm text-gray-400">
                  {format(parseISO(selectedVacationRequest.start_date), 'dd MMM')} - {format(parseISO(selectedVacationRequest.end_date), 'dd MMM yyyy')}
                </p>
                {selectedVacationRequest.reason && <p className="text-sm text-gray-500 mt-2">{selectedVacationRequest.reason}</p>}
              </div>
              <div className="space-y-2"><Label className="text-gray-300">Status</Label>
                <Select value={vacationReviewData.status} onValueChange={(v) => setVacationReviewData(p => ({ ...p, status: v }))}>
                  <SelectTrigger className="bg-[#1E2530] border-white/20 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#2A3240] border-white/10">
                    <SelectItem value="pending" className="text-amber-400">Pending</SelectItem>
                    <SelectItem value="approved" className="text-green-400">Approved</SelectItem>
                    <SelectItem value="denied" className="text-red-400">Denied</SelectItem>
                  </SelectContent>
                </Select></div>
              <div className="space-y-2"><Label className="text-gray-300">Admin Notes (Optional)</Label>
                <Textarea value={vacationReviewData.admin_notes} onChange={(e) => setVacationReviewData(p => ({ ...p, admin_notes: e.target.value }))}
                  placeholder="Add a note for the employee..." rows={2} className="bg-[#1E2530] border-white/20 text-white resize-none" /></div>
              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={() => setVacationReviewDialogOpen(false)} className="flex-1 border-white/20 text-gray-300">Cancel</Button>
                <Button onClick={handleReviewVacation} disabled={submitting} className="flex-1 bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D]">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Update'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Export Dialog */}
      <Dialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
        <DialogContent className="bg-[#2A3240] border-white/10 max-w-md">
          <DialogHeader><DialogTitle className="text-white flex items-center gap-2"><Download className="w-5 h-5 text-[#F5A623]" />Export Schedule to CSV</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label className="text-gray-300">Start Date</Label>
                <Input type="date" value={exportDateRange.start_date} onChange={(e) => setExportDateRange(p => ({ ...p, start_date: e.target.value }))}
                  className="bg-[#1E2530] border-white/20 text-white" /></div>
              <div className="space-y-2"><Label className="text-gray-300">End Date</Label>
                <Input type="date" value={exportDateRange.end_date} onChange={(e) => setExportDateRange(p => ({ ...p, end_date: e.target.value }))}
                  className="bg-[#1E2530] border-white/20 text-white" /></div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={() => {
                const s = startOfWeek(new Date(), { weekStartsOn: 1 }), e = endOfWeek(new Date(), { weekStartsOn: 1 });
                setExportDateRange({ start_date: format(s, 'yyyy-MM-dd'), end_date: format(e, 'yyyy-MM-dd') });
              }} className="border-white/20 text-gray-300 text-xs">This Week</Button>
              <Button variant="outline" size="sm" onClick={() => {
                const s = startOfMonth(new Date()), e = endOfMonth(new Date());
                setExportDateRange({ start_date: format(s, 'yyyy-MM-dd'), end_date: format(e, 'yyyy-MM-dd') });
              }} className="border-white/20 text-gray-300 text-xs">This Month</Button>
              <Button variant="outline" size="sm" onClick={() => {
                const s = startOfWeek(addWeeks(new Date(), 1), { weekStartsOn: 1 }), e = endOfWeek(addWeeks(new Date(), 1), { weekStartsOn: 1 });
                setExportDateRange({ start_date: format(s, 'yyyy-MM-dd'), end_date: format(e, 'yyyy-MM-dd') });
              }} className="border-white/20 text-gray-300 text-xs">Next Week</Button>
            </div>
            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={() => setExportDialogOpen(false)} className="flex-1 border-white/20 text-gray-300">Cancel</Button>
              <Button onClick={exportToCSV} className="flex-1 bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D]">
                <Download className="w-4 h-4 mr-2" />Export CSV
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
